// Previsão/Efetivo — mesmo fornecedor em 2+ grupos do orçamento = um card por grupo (D-0096, Rafael 24/10: "deve
// aparecer 2x na previsão de pagamento, uma em cada grupo"). A API manda cada card com `fornecedor_id` =
// '{fornecedor}:{grupo}' (ou '{fornecedor}:sem-grupo' para parcela antiga sem grupo), `fornecedor_real_id` e
// `grupo_card` ({ id, nome }). O plano é UM só: status, NF e adiantamento são do fornecedor e aparecem
// iguais nos cards dele — somas e alertas contam o fornecedor uma vez. Quem paga é por item (D-0137) e vem da API.

export const MSG_SEM_GRUPO = 'Parcela antiga sem grupo. Parcela nova vai no card do grupo (Hospedagem, Transfer...).';

// Campos do plano que a tela guarda por card (estado `efetivos`) — salvar num card vale para os irmãos. Só passa para
// os irmãos o que foi salvo: o cabeçalho do Efetivo (Salvar) ou o campo da Previsão que acabou de ser gravado.
export const CAMPOS_DO_EFETIVO = [
    'tipo_documento', 'tipo_faturamento', 'numero_referencia', 'email_fornecedor_nf', 'valor_total_nf',
    'usar_adiantamento', 'valor_adiantamento', 'percent_adiantamento',
];

export function fornecedorRealDoCard(card) {
    if (!card) return '';
    return String(card.fornecedor_real_id || card.fornecedor_id || '');
}

export function ehCardPorGrupo(card) {
    return !!(card && card.grupo_card);
}

export function rotuloGrupoCard(card) {
    if (!ehCardPorGrupo(card)) return null;
    return card.sem_grupo ? 'sem grupo' : (card.grupo_card.nome || 'Grupo');
}

export const MSG_GRUPO_SEM_ITEM = 'Este fornecedor não tem mais item neste grupo do orçamento. Ajuste ou exclua as parcelas daqui.';

// Card que não recebe parcela nova: "sem grupo" (parcelas antigas) e grupo em que o fornecedor já não tem item (a
// parcela ficou lá para o operador ajustar). null = pode lançar (a trava do valor segue a de sempre).
export function bloqueioParcelaNoCard(card) {
    if (!ehCardPorGrupo(card)) return null;
    if (card.sem_grupo) return MSG_SEM_GRUPO;
    if (!(card.itens || []).length) return MSG_GRUPO_SEM_ITEM;
    return null;
}

// D-0115: o card por grupo também respeita o saldo do FORNECEDOR — a trava do servidor é pelo fornecedor, e parcela
// sem grupo ou de outro card gasta o mesmo teto. A API manda `saldo_fornecedor_brl` (e o total/parcelado) no card por
// grupo; a parcela em edição volta ao saldo (o servidor a tira da conta). null = sem esse teto (fornecedor num grupo
// só, API antiga).
export function saldoDoFornecedorNoCard(card, valorEmEdicao = 0) {
    if (!ehCardPorGrupo(card) || card.saldo_fornecedor_brl === null || card.saldo_fornecedor_brl === undefined) return null;
    const emEdicao = Number(valorEmEdicao) || 0;
    return {
        total: Number(card.total_fornecedor_brl || 0),
        parcelado: Number(card.parcelado_fornecedor || 0) - emEdicao,
        saldo: Number(card.saldo_fornecedor_brl) + emEdicao,
    };
}

// Um card por fornecedor (o primeiro), para somas e alertas do plano. Card "a definir" conta pelo próprio id.
export function umCardPorFornecedor(cards = []) {
    const vistos = new Set();
    return (Array.isArray(cards) ? cards : []).filter((card) => {
        const chave = fornecedorRealDoCard(card);
        if (vistos.has(chave)) return false;
        vistos.add(chave);
        return true;
    });
}

// Fornecedores com parcela de previsão em algum dos cards dele.
export function fornecedoresComParcelas(cards = []) {
    const com = new Set();
    (Array.isArray(cards) ? cards : []).forEach((card) => {
        if ((card.parcelas || []).length > 0) com.add(fornecedorRealDoCard(card));
    });
    return com.size;
}

// Cards do mesmo fornecedor (menos o próprio).
export function cardsIrmaos(cards = [], fornId) {
    const card = (Array.isArray(cards) ? cards : []).find((c) => String(c.fornecedor_id) === String(fornId));
    if (!card || !ehCardPorGrupo(card)) return [];
    const real = fornecedorRealDoCard(card);
    return cards.filter((c) => String(c.fornecedor_id) !== String(fornId) && fornecedorRealDoCard(c) === real)
        .map((c) => String(c.fornecedor_id));
}

// Depois de salvar campos do plano num card, os irmãos mostram o mesmo (sem mutar o estado). `campos` = os que foram
// gravados — o que o operador digitou e ainda não salvou no irmão fica como está.
export function sincronizarCamposDoPlano(efetivos = {}, cards = [], fornId, campos = []) {
    const origem = efetivos[fornId];
    const irmaos = cardsIrmaos(cards, fornId).filter((id) => efetivos[id]);
    if (!origem || irmaos.length === 0) return efetivos;
    const novo = { ...efetivos };
    irmaos.forEach((id) => {
        const atualizado = { ...novo[id] };
        campos.forEach((campo) => {
            if (Object.prototype.hasOwnProperty.call(origem, campo)) atualizado[campo] = origem[campo];
        });
        novo[id] = atualizado;
    });
    return novo;
}

// Efetivo: a NF é do fornecedor (plano único) — a conferência com o total da NF soma as parcelas de todos os cards dele.
export function totalParcelasEfetivoDoFornecedor(efetivos = {}, cards = [], fornId) {
    const ids = [String(fornId), ...cardsIrmaos(cards, fornId)];
    return ids.reduce((soma, id) => soma + ((efetivos[id] && efetivos[id].parcelas) || [])
        .reduce((s, p) => s + (parseFloat(p.valor) || 0), 0), 0);
}
